import { getLocalTimeZone, now } from "@internationalized/date"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { AppUserPUTFailReason } from "ipctg-inflow-types/InFlowEnums"
import { Controller, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { getStr } from "@/utilities/etc"
import { ExpectedError, GatedAppUserError } from "@/utilities/expectedErrors"
import { licensePlateValidationRule, requiredRule } from "@/utilities/form"
import { getAreaHierarchyIDs } from "@/utilities/getAreaHierarchyIDs"
import { DEFAULT_TIME_ZONE } from "@/utilities/globals"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useScopedNavigate } from "@/utilities/hooks/useScopedRouting"
import { useTheme } from "@/utilities/hooks/useTheme"
import { normalizeLicensePlate } from "@/utilities/normalizers"

export const Route = createFileRoute("/_authenticated/account/vehicles/create")(
  {
    // staticData: {
    //   pageName: "Add Vehicle",
    // },
    validateSearch: (search: Record<string, unknown>) => ({
      redirect: getStr(search.redirect),
      plate: getStr(search.plate),
    }),
    component: RouteComponent,
  }
)

function RouteComponent() {
  const { redirect, plate } = Route.useSearch()
  return <VehicleCreateRouteComponent redirect={redirect} plate={plate} />
}

export function VehicleCreateRouteComponent({
  redirect,
  plate,
}: {
  redirect?: string
  plate?: string
}) {
  const { t } = useTranslation(["errors", "common"])
  const router = useRouter()
  const scopedNavigate = useScopedNavigate()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const queryClient = useQueryClient()
  const { locationInformation } = useTheme()
  const locationID = locationInformation?.locationID

  const form = useForm<SavedPlate>({
    defaultValues: {
      plate: plate || "",
      nickname: "",
      lastUsed: "",
      state: "XX",
    },
    mode: "onBlur",
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Card>
      <Heading level={1} as={2}>
        {t("account.vehicles.createHeader", { ns: "common" })}
      </Heading>
      <Text level={2} style={{ marginTop: "var(--space-2)" }}>
        <Trans i18nKey="account.vehicles.createDescription" ns="common">
          Add a vehicle to your account for faster checkouts.
          <em className="bold">Driving a rental?</em> Don&apos;t forget to
          remove it from your account once you return it.
        </Trans>
      </Text>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset>
          <input type="hidden" {...form.register("state")} />
          <input type="hidden" {...form.register("lastUsed")} />
          <Controller<SavedPlate, "nickname">
            name="nickname"
            control={form.control}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onFocus={() => form.clearErrors("root")}
                isInvalid={invalid}
              >
                <Label>
                  {t("account.vehicles.nickname", { ns: "common" })}{" "}
                  <span className="optional">
                    ({t("common.optional", { ns: "common" })})
                  </span>
                </Label>
                <TextField.Input innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<SavedPlate, "plate">
            name="plate"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              validate: (value) => {
                const normalized = normalizeLicensePlate(value)
                return licensePlateValidationRule(t)(normalized)
              },
            }}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onChange={(v) => onChange(normalizeLicensePlate(v))}
                onFocus={() => form.clearErrors("root")}
                isInvalid={invalid}
              >
                <Label>{t("account.vehicles.plate", { ns: "common" })}</Label>
                <TextField.Input innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button buttonType="primary" type="submit" isPending={isPending}>
            {t("common.save", { ns: "common" })}
          </Button>
          {redirect && (
            <Button
              buttonType="primary"
              presentation="link"
              style={{
                textDecorationLine: "underline",
                fontSize: "var(--font-size-2)",
              }}
              onPress={() => router.history.push(redirect)}
            >
              {t("common.skipForNow", { ns: "common" })}
            </Button>
          )}
        </fieldset>
      </form>
    </Card>
  )

  function onSubmit(data: SavedPlate) {
    mutate(data)
  }

  async function mutationFn(data: SavedPlate) {
    const session = await fetchAuthSession()
    const cognitoIDToken = session.tokens?.idToken

    if (!cognitoIDToken || !me)
      throw new GatedAppUserError(AppUserPUTFailReason.Unauthorized)

    const rsp = await api.appUser.PUT({
      cognitoIDToken: cognitoIDToken.toString(),
      locationID: locationID || null,
      enterpriseID: locationID
        ? getAreaHierarchyIDs({ locationID }).enterpriseID
        : null,
      ...me.inflowUser,
      savedPlates: [
        ...me.inflowUser.savedPlates,
        {
          ...data,
          lastUsed: now(
            getLocalTimeZone() || DEFAULT_TIME_ZONE
          ).toAbsoluteString(),
        },
      ],
    })

    if ("failReason" in rsp) {
      throw new GatedAppUserError(rsp.failReason)
    }

    return rsp
  }

  function onSuccess() {
    queryClient.invalidateQueries({
      ...meQueryOptions(),
      refetchType: "all",
    })

    if (redirect) {
      router.history.push(redirect)
    } else {
      scopedNavigate({ to: "/account/vehicles" })
    }
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      form.setError("root", {
        message: t(error.translationKey, { ns: "errors" }),
      })
    } else {
      form.setError("root", { message: error.message })
    }
  }
}
